import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Slider,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Build as ToolsIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Merge as MergeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import {
  getPotentialMatches,
  linkCompetitor,
  addCompetitorAsSkater,
  dismissMatch,
  getMatchTypeLabel,
  getMatchTypeColor,
} from '../services/toolsService';

export default function Tools() {
  const { isAdmin, isInterclubAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState({});
  const [minSimilarity, setMinSimilarity] = useState(70);
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [updateResults, setUpdateResults] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPotentialMatches(minSimilarity);
      setMatches(data.potentialMatches || []);
      setStats({
        total: data.totalMatches,
        registered: data.registeredSkaterCount,
        competitors: data.competitorCount,
      });
    } catch (err) {
      console.error('Error loading potential matches:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [minSimilarity]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  const handleLink = async () => {
    if (!selectedMatch) return;

    try {
      setActionLoading('link');
      const result = await linkCompetitor(
        selectedMatch.competitor.firstName,
        selectedMatch.competitor.lastName,
        selectedMatch.selectedSkaterId || selectedMatch.bestMatch.skaterId,
        updateResults
      );
      setSuccessMessage(result.message);
      setDialogOpen(false);
      setSelectedMatch(null);
      await loadMatches();
    } catch (err) {
      console.error('Error linking competitor:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddAsSkater = async () => {
    if (!selectedMatch) return;

    try {
      setActionLoading('add');
      const result = await addCompetitorAsSkater(
        selectedMatch.competitor.firstName,
        selectedMatch.competitor.lastName,
        selectedMatch.competitor.clubs[0] || null,
        selectedMatch.competitor.levels[0] || null
      );
      setSuccessMessage(result.message);
      setDialogOpen(false);
      setSelectedMatch(null);
      await loadMatches();
    } catch (err) {
      console.error('Error adding competitor as skater:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDismiss = async () => {
    if (!selectedMatch) return;

    try {
      setActionLoading('dismiss');
      await dismissMatch(
        selectedMatch.competitor.firstName,
        selectedMatch.competitor.lastName,
        selectedMatch.bestMatch?.skaterId
      );
      setSuccessMessage(`Dismissed match for "${selectedMatch.competitor.fullName}"`);
      setDialogOpen(false);
      setSelectedMatch(null);
      // Remove from local state
      setMatches((prev) => prev.filter((m) => m.competitor.fullName !== selectedMatch.competitor.fullName));
    } catch (err) {
      console.error('Error dismissing match:', err);
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const isSameCompetitor = (match) =>
    selectedMatch?.competitor?.fullName === match?.competitor?.fullName;

  const getSelectedRegistered = (match) => {
    const selectedId = isSameCompetitor(match)
      ? selectedMatch.selectedSkaterId || match.bestMatch?.skaterId
      : match.bestMatch?.skaterId;
    return match.matches.find((m) => m.skaterId === selectedId) || match.bestMatch;
  };

  const openDialog = (match, type) => {
    const selectedRegistered = getSelectedRegistered(match);
    setSelectedMatch({
      ...match,
      selectedSkaterId: selectedRegistered?.skaterId || match.bestMatch?.skaterId,
    });
    setDialogType(type);
    setDialogOpen(true);
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'success';
    if (confidence >= 80) return 'warning';
    return 'default';
  };

  if (!isAdmin && !isInterclubAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <ToolsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Data Tools
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reconcile skater data and fix name mismatches
          </Typography>
        </Box>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Skater Reconciliation Section */}
      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MergeIcon /> Skater Name Reconciliation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Find and fix name mismatches between competition results and registered skaters
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadMatches} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {/* Stats */}
        {!loading && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Chip
              icon={<WarningIcon />}
              label={`${stats.total || 0} Potential Matches`}
              color={stats.total > 0 ? 'warning' : 'success'}
              variant="outlined"
            />
            <Chip label={`${stats.registered || 0} Registered Skaters`} color="primary" variant="outlined" />
            <Chip label={`${stats.competitors || 0} Competition Names`} color="default" variant="outlined" />
          </Box>
        )}

        {/* Similarity Threshold */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Minimum Similarity Threshold: <strong>{minSimilarity}%</strong>
          </Typography>
          <Slider
            value={minSimilarity}
            onChange={(_, value) => setMinSimilarity(value)}
            onChangeCommitted={() => loadMatches()}
            min={50}
            max={95}
            step={5}
            marks={[
              { value: 50, label: '50%' },
              { value: 70, label: '70%' },
              { value: 90, label: '90%' },
            ]}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ py: 4 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Analyzing skater names...
            </Typography>
          </Box>
        )}

        {/* No Matches */}
        {!loading && matches.length === 0 && (
          <Alert severity="success" icon={<CheckIcon />}>
            No potential name mismatches found at the current similarity threshold.
          </Alert>
        )}

        {/* Matches List */}
        {!loading && matches.length > 0 && (
          <Box>
            {matches.map((match, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {match.competitor.fullName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {match.competitor.competitionCount} competition(s)
                        {' • '}
                        {match.competitor.clubs?.length
                          ? `Club on results: ${match.competitor.clubs.join(', ')}`
                          : 'Club not listed on results'}
                        {match.competitor.hostClubs?.length
                          ? ` • Host: ${match.competitor.hostClubs.join(', ')}`
                          : ''}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${match.bestMatch.confidence}% match`}
                      color={getConfidenceColor(match.bestMatch.confidence)}
                      size="small"
                    />
                    <Chip
                      label={getMatchTypeLabel(match.bestMatch.matchType)}
                      color={getMatchTypeColor(match.bestMatch.matchType)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>From Competition Results:</strong>
                    </Typography>
                    <Box sx={{ pl: 2, mb: 2 }}>
                      <Typography variant="body2">
                        Name on results: <strong>{match.competitor.fullName}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Club on results:{' '}
                        <strong>
                          {match.competitor.clubs?.length ? match.competitor.clubs.join(', ') : 'Not listed on results'}
                        </strong>
                      </Typography>
                      {match.competitor.appearances?.length > 0 ? (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2">Competitions:</Typography>
                          {match.competitor.appearances.map((appearance, appearanceIdx) => (
                            <Typography key={appearanceIdx} variant="body2" sx={{ pl: 1 }}>
                              {appearance.competition}
                              {appearance.hostClub ? ` • host ${appearance.hostClub}` : ''}
                              {appearance.resultClub
                                ? ` • club on sheet ${appearance.resultClub}`
                                : ' • club not on sheet'}
                            </Typography>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          Competitions: {match.competitor.competitions.slice(0, 3).join(', ')}
                          {match.competitor.competitions.length > 3 && ` +${match.competitor.competitions.length - 3} more`}
                        </Typography>
                      )}
                      <Typography variant="body2">Levels: {match.competitor.levels.join(', ') || 'N/A'}</Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Potential Matches:</strong>
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Registered Skater</TableCell>
                          <TableCell>Club</TableCell>
                          <TableCell>Level</TableCell>
                          <TableCell align="center">Confidence</TableCell>
                          <TableCell>Match Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {match.matches.map((m, idx) => {
                          const selectedRegistered = getSelectedRegistered(match);
                          const isSelected = m.skaterId === selectedRegistered?.skaterId;
                          return (
                          <TableRow
                            key={idx}
                            sx={{
                              bgcolor: isSelected ? 'action.selected' : 'inherit',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                            onClick={() => {
                              setSelectedMatch({ ...match, selectedSkaterId: m.skaterId });
                            }}
                          >
                            <TableCell sx={{ fontWeight: isSelected ? 600 : 400 }}>{m.skaterName}</TableCell>
                            <TableCell>{m.club || '-'}</TableCell>
                            <TableCell>{m.level || '-'}</TableCell>
                            <TableCell align="center">
                              <Chip label={`${m.confidence}%`} color={getConfidenceColor(m.confidence)} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getMatchTypeLabel(m.matchType)}
                                color={getMatchTypeColor(m.matchType)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 2 }}>
                    {!match.competitor.clubs?.length && (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        The competition sheet does not list a home club for this name. Compare the event host
                        {match.competitor.hostClubs?.length ? ` (${match.competitor.hostClubs.join(', ')})` : ''}
                        {' '}with the registered club before linking.
                      </Alert>
                    )}
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Link results for <strong>{match.competitor.fullName}</strong>
                      {' → '}
                      registered skater{' '}
                      <strong>{getSelectedRegistered(match)?.skaterName}</strong>
                    </Typography>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<LinkIcon />}
                      onClick={() => openDialog(match, 'link')}
                      size="small"
                    >
                      Link to {getSelectedRegistered(match)?.skaterName}
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<PersonAddIcon />}
                      onClick={() => openDialog(match, 'add')}
                      size="small"
                    >
                      Add as New Skater
                    </Button>
                    <Button variant="outlined" color="default" startIcon={<CloseIcon />} onClick={() => openDialog(match, 'dismiss')} size="small">
                      Not a Match
                    </Button>
                  </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Paper>

      {/* Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        {dialogType === 'link' && selectedMatch && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinkIcon color="success" />
                Link Competition Name to Registered Skater
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                This will update all competition results for "<strong>{selectedMatch.competitor.fullName}</strong>" to use the
                registered name.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Competition Name:
                </Typography>
                <Typography variant="h6">{selectedMatch.competitor.fullName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Will be linked to:
                </Typography>
                <Typography variant="h6" color="success.main">
                  {selectedMatch.matches.find((m) => m.skaterId === selectedMatch.selectedSkaterId)?.skaterName ||
                    selectedMatch.bestMatch.skaterName}
                </Typography>
              </Box>
              <FormControlLabel
                control={<Checkbox checked={updateResults} onChange={(e) => setUpdateResults(e.target.checked)} />}
                label="Update competition results with registered name"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleLink}
                disabled={actionLoading === 'link'}
                startIcon={actionLoading === 'link' ? <CircularProgress size={16} /> : <LinkIcon />}
              >
                Link Names
              </Button>
            </DialogActions>
          </>
        )}

        {dialogType === 'add' && selectedMatch && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonAddIcon color="primary" />
                Add as New Skater
              </Box>
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will create a new skater record. Only do this if this is definitely a different person from the suggested
                matches.
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  New Skater:
                </Typography>
                <Typography variant="h6">{selectedMatch.competitor.fullName}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Club (from competition):
                </Typography>
                <Typography>{selectedMatch.competitor.clubs[0] || 'None'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Level (from competition):
                </Typography>
                <Typography>{selectedMatch.competitor.levels[0] || 'None'}</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddAsSkater}
                disabled={actionLoading === 'add'}
                startIcon={actionLoading === 'add' ? <CircularProgress size={16} /> : <PersonAddIcon />}
              >
                Create Skater
              </Button>
            </DialogActions>
          </>
        )}

        {dialogType === 'dismiss' && selectedMatch && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloseIcon />
                Dismiss Match
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Mark "<strong>{selectedMatch.competitor.fullName}</strong>" as not matching any of the suggested skaters?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This will hide this match from future results. Use this when the names are similar but they are definitely
                different people.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                variant="outlined"
                onClick={handleDismiss}
                disabled={actionLoading === 'dismiss'}
                startIcon={actionLoading === 'dismiss' ? <CircularProgress size={16} /> : <CloseIcon />}
              >
                Dismiss Match
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
